/**
 * FinalAnswerConfirmationV2 - Friendly confirmation before submitting the final answer
 *
 * Shown when a student submits the last question of a stage (unless they have opted out). Reassures
 * rather than alarms: it simply reminds them that answers lock once submitted and offers a chance to
 * review. Includes a "Don't show this again" opt-out persisted per device.
 */

import { memo, useCallback, useEffect, useState } from 'react'
import { useChallengeV2 as useChallengeStoreV2 } from '../../../hooks/useChallenge'

// Friendly check-circle icon (reassuring, not a warning triangle)
const ReadyIcon = () => (
  <svg className="w-16 h-16" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="28" className="fill-emerald-100 dark:fill-emerald-900/30" />
    <path
      d="M21 32.5l7 7 15-15"
      className="stroke-emerald-500"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const FinalAnswerConfirmationV2 = memo(() => {
  // Store state
  const showFinalAnswerConfirmation = useChallengeStoreV2(
    (state) => state.showFinalAnswerConfirmation
  )
  const isCompletingStage = useChallengeStoreV2((state) => state.isCompletingStage)

  // Store actions
  const closeFinalAnswerConfirmation = useChallengeStoreV2(
    (state) => state.closeFinalAnswerConfirmation
  )
  const setFinalAnswerWarningDismissed = useChallengeStoreV2(
    (state) => state.setFinalAnswerWarningDismissed
  )
  const handleSubmitStage = useChallengeStoreV2((state) => state.handleSubmitStage)

  // Local checkbox state, reset whenever the modal opens
  const [dontShowAgain, setDontShowAgain] = useState(false)

  useEffect(() => {
    if (showFinalAnswerConfirmation) {
      setDontShowAgain(false)
    }
  }, [showFinalAnswerConfirmation])

  // Confirm and submit
  const handleConfirm = useCallback(() => {
    if (dontShowAgain) {
      setFinalAnswerWarningDismissed(true)
    }
    closeFinalAnswerConfirmation()
    handleSubmitStage()
  }, [
    dontShowAgain,
    setFinalAnswerWarningDismissed,
    closeFinalAnswerConfirmation,
    handleSubmitStage
  ])

  // Go back to review without submitting
  const handleBack = useCallback(() => {
    closeFinalAnswerConfirmation()
  }, [closeFinalAnswerConfirmation])

  // Prevent body scroll while open
  useEffect(() => {
    if (showFinalAnswerConfirmation) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showFinalAnswerConfirmation])

  if (!showFinalAnswerConfirmation) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="final-answer-title"
      aria-describedby="final-answer-desc"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleBack} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-[modalIn_0.25s_ease-out]">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <ReadyIcon />
        </div>

        {/* Title */}
        <h2
          id="final-answer-title"
          className="text-2xl font-bold text-gray-900 dark:text-white mb-2"
        >
          Ready to submit?
        </h2>

        <p id="final-answer-desc" className="text-gray-600 dark:text-gray-400 mb-6">
          Once you submit, your answers are locked in and cannot be changed. Take a moment to review
          if you would like, otherwise you are good to go.
        </p>

        {/* Don't show again */}
        <label className="flex items-center justify-center gap-2 mb-6 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Don&apos;t show this again
          </span>
        </label>

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleConfirm}
            disabled={isCompletingStage}
            className="w-full px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Submit answers
          </button>
          <button
            onClick={handleBack}
            disabled={isCompletingStage}
            className="w-full px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-colors disabled:opacity-60"
          >
            Back to review
          </button>
        </div>
      </div>
    </div>
  )
})

FinalAnswerConfirmationV2.displayName = 'FinalAnswerConfirmationV2'

export default FinalAnswerConfirmationV2
